<script setup>
import { useAccount } from 'dashboard/composables/useAccount';
import HeatmapContainer from './components/HeatmapContainer.vue';
import AgentLiveReportContainer from './components/AgentLiveReportContainer.vue';
import TeamLiveReportContainer from './components/TeamLiveReportContainer.vue';
import StatsLiveReportsContainer from './components/StatsLiveReportsContainer.vue';
import OverviewLast7DaysSnapshot from './components/OverviewLast7DaysSnapshot.vue';

const { accountScopedRoute } = useAccount();
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="ov-page">
    <div class="ov-shell">
      <div class="ov-header">
        <div class="ov-header-left">
          <h1 class="ov-title">{{ $t('OVERVIEW_REPORTS.HEADER') }}</h1>
          <span class="ov-live-badge">
            <span class="ov-live-dot" />
            Live
          </span>
        </div>
        <div class="ov-header-right">
          <router-link
            :to="accountScopedRoute('token_usage_reports')"
            class="ov-link-btn"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="16"
              height="16"
            >
              <path
                stroke="currentcolor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M7 7l5 5-5 5m8-10v10"
              />
            </svg>
            Token usage
          </router-link>
        </div>
      </div>

      <div class="ov-content">
        <section class="ov-section ov-section--snapshot">
          <OverviewLast7DaysSnapshot />
        </section>

        <section class="ov-section ov-section--stats">
          <StatsLiveReportsContainer />
        </section>

        <section class="ov-section">
          <HeatmapContainer />
        </section>

        <div class="ov-grid-2">
          <section class="ov-section">
            <AgentLiveReportContainer />
          </section>
          <section class="ov-section">
            <TeamLiveReportContainer />
          </section>
        </div>
      </div>
    </div>
  </div>
</template>
<!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->

<style scoped>
.ov-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}

.ov-shell {
  min-width: 650px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: visible;
}

.ov-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
  position: sticky;
  top: 0;
  z-index: 50;
  background: #ffffff;
}

.ov-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ov-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}

.ov-live-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 999px;
  background: #dcfce7;
  color: #15803d;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.ov-live-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #16a34a;
  animation: ov-pulse 1.5s ease-in-out infinite;
}

@keyframes ov-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

.ov-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ov-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
}

/* ── Content ── */
.ov-content {
  padding: 16px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  /* Force light-mode CSS variable values for all live sub-components */
  --n-slate-12: #111827;
  --n-slate-11: #374151;
  --n-slate-10: #6b7280;
  --n-slate-9: #9ca3af;
  --n-slate-8: #d1d5db;
  --n-slate-7: #e5e7eb;
  --n-slate-6: #f3f4f6;
  --n-slate-5: #f9fafb;
  --n-slate-4: #ffffff;
  --n-slate-3: #ffffff;
  --n-slate-2: #f8fafc;
  --n-solid-1: #ffffff;
  --n-solid-2: #f9fafb;
  --n-solid-3: #f3f4f6;
  --n-alpha-1: rgba(0, 0, 0, 0.03);
  --n-alpha-2: rgba(0, 0, 0, 0.04);
  --n-weak: rgba(0, 0, 0, 0.08);
  --n-container: rgba(0, 0, 0, 0.1);
  --n-brand: #1d75e5;
}

/* Dark-mode Tailwind utility overrides */
.ov-content :deep(.dark\:text-white) {
  color: #111827 !important;
}
.ov-content :deep(.dark\:text-white\/90) {
  color: #111827 !important;
}
.ov-content :deep(.dark\:text-white\/60) {
  color: #6b7280 !important;
}
.ov-content :deep(.dark\:text-white\/40) {
  color: #9ca3af !important;
}
.ov-content :deep(.dark\:bg-slate-900) {
  background: #ffffff !important;
}
.ov-content :deep(.dark\:bg-slate-800) {
  background: #f9fafb !important;
}
.ov-content :deep(.dark\:border-slate-700) {
  border-color: #e5e7eb !important;
}

/* Design token colour fixes */
.ov-content :deep(.text-n-slate-12) {
  color: #111827 !important;
}
.ov-content :deep(.text-n-slate-11) {
  color: #374151 !important;
}
.ov-content :deep(.text-n-slate-10) {
  color: #6b7280 !important;
}
.ov-content :deep(.text-n-slate-9) {
  color: #9ca3af !important;
}
.ov-content :deep(.bg-n-solid-1) {
  background: #ffffff !important;
}
.ov-content :deep(.bg-n-solid-2) {
  background: #f9fafb !important;
}
.ov-content :deep(.bg-n-solid-3) {
  background: #f3f4f6 !important;
}
.ov-content :deep(.bg-n-slate-2) {
  background: #f8fafc !important;
}
.ov-content :deep(.border-n-weak) {
  border-color: #e5e7eb !important;
}
.ov-content :deep(.outline-n-container) {
  outline-color: #e2e8f0 !important;
}

/* Card / panel chrome */
.ov-content :deep(.rounded-xl) {
  border-radius: 8px !important;
}
.ov-content :deep(.shadow) {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.07) !important;
}

/* Table th */
.ov-content :deep(th) {
  color: #111827 !important;
  background: transparent !important;
  font-weight: 600 !important;
}
.ov-content :deep(td) {
  color: #374151 !important;
}

/* Section wrapper */
.ov-section {
  /* allows sub-components to size themselves naturally */
}

.ov-grid-2 {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}

@media (max-width: 1100px) {
  .ov-grid-2 {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

/* Suppress hover transitions */
.ov-content :deep(tr) {
  transition: none !important;
}

/* Remove hover effects across the dashboard (no hover chrome) */
.ov-shell :deep(*) {
  transition: none !important;
}
.ov-shell :deep(tr:hover),
.ov-shell :deep([class*='hover\\:bg']:hover),
.ov-shell :deep([class*='hover\\:shadow']:hover),
.ov-shell :deep([class*='hover\\:ring']:hover),
.ov-shell :deep([class*='hover\\:outline']:hover),
.ov-shell :deep([class*='hover\\:opacity']:hover) {
  box-shadow: none !important;
  transform: none !important;
}

.ov-shell :deep(.group:hover .group-hover\\:bg-n-alpha-2) {
  background: transparent !important;
}

.ov-shell :deep(*:hover) {
  transform: none !important;
  box-shadow: none !important;
}

.ov-shell :deep(.wlab-card:hover),
.ov-shell :deep(.wlab-card-elevated:hover),
.ov-shell :deep(.wlab-stat-card:hover),
.ov-shell :deep(.wlab-interactive:hover),
.ov-shell :deep(.bg-n-solid-2.rounded-xl:hover),
.ov-shell :deep(.bg-n-solid-2.rounded-2xl:hover),
.ov-shell :deep(.bg-n-solid-2.rounded-lg:hover),
.ov-shell :deep(.shadow.rounded-xl:hover),
.ov-shell :deep(.shadow.rounded-2xl:hover),
.ov-shell :deep(.shadow.rounded-lg:hover),
.ov-shell :deep(.shadow-md.rounded-xl:hover),
.ov-shell :deep(.shadow-md.rounded-2xl:hover),
.ov-shell :deep(.shadow-lg.rounded-xl:hover),
.ov-shell :deep(.outline.outline-n-container.rounded-xl:hover),
.ov-shell :deep(.outline.outline-n-container.rounded-2xl:hover) {
  transform: none !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.07) !important;
  border-color: #e5e7eb !important;
}

::global(body.dark) .ov-page,
::global(body.dark) .ov-shell,
::global(body.dark) .ov-header,
::global(body.theme-mix) .ov-page,
::global(body.theme-mix) .ov-shell,
::global(body.theme-mix) .ov-header {
  background: #ffffff !important;
  color: #111827 !important;
}

@media (max-width: 1100px) {
  .ov-content {
    padding: 12px 16px 16px;
  }
}
</style>
